import React, { useState, useEffect } from 'react';
import { Save, Bot, User, Info } from 'lucide-react';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import {
  ApplicationProfile,
  ApplicationProfileUpdate,
  ApplicationType,
  DeploymentExposure,
  InputSource,
  AuthenticationModel,
  DataSensitivity,
  APPLICATION_TYPE_LABELS,
  DEPLOYMENT_EXPOSURE_LABELS,
  INPUT_SOURCE_LABELS,
  AUTHENTICATION_MODEL_LABELS,
  DATA_SENSITIVITY_LABELS,
  ApplicationResponse,
} from '../../types/application';
import toast from '../../design-system/toast';

interface ApplicationProfileEditorProps {
  organizationId: string;
  applicationId: string;
  profile?: ApplicationProfile;
  onUpdate?: (updatedApp: ApplicationResponse) => void;
  readOnly?: boolean;
}

const ApplicationProfileEditor: React.FC<ApplicationProfileEditorProps> = ({
  organizationId,
  applicationId,
  profile,
  onUpdate,
  readOnly = false,
}) => {
  const api = useAuthenticatedApi();
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [applicationType, setApplicationType] = useState<ApplicationType>(
    profile?.application_type || 'api_server'
  );
  const [deploymentExposure, setDeploymentExposure] = useState<DeploymentExposure>(
    profile?.deployment_exposure || 'internal'
  );
  const [inputSources, setInputSources] = useState<Set<InputSource>>(
    new Set(profile?.input_sources || [])
  );
  const [authenticationModel, setAuthenticationModel] = useState<AuthenticationModel>(
    profile?.authentication_model || 'none'
  );
  const [dataSensitivity, setDataSensitivity] = useState<DataSensitivity>(
    profile?.data_sensitivity || 'internal'
  );
  const [contextNotes, setContextNotes] = useState(profile?.context_notes || '');

  // Reset form when profile changes
  useEffect(() => {
    if (profile) {
      setApplicationType(profile.application_type);
      setDeploymentExposure(profile.deployment_exposure);
      setInputSources(new Set(profile.input_sources));
      setAuthenticationModel(profile.authentication_model);
      setDataSensitivity(profile.data_sensitivity);
      setContextNotes(profile.context_notes || '');
      setHasChanges(false);
    }
  }, [profile]);

  const handleInputSourceToggle = (source: InputSource) => {
    setInputSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) {
        next.delete(source);
      } else {
        next.add(source);
      }
      return next;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const update: ApplicationProfileUpdate = {
        application_type: applicationType,
        deployment_exposure: deploymentExposure,
        input_sources: Array.from(inputSources),
        authentication_model: authenticationModel,
        data_sensitivity: dataSensitivity,
        context_notes: contextNotes,
      };

      const updatedApp = await api.put<ApplicationResponse>(
        apiEndpoints.applications.updateProfile(organizationId, applicationId),
        update
      );

      toast.success('Profile updated successfully');
      setHasChanges(false);
      onUpdate?.(updatedApp);
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      toast.error(err.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const selectClassName = `w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg 
    bg-ui-background-secondary text-ui-text-primary
    focus:ring-2 focus:ring-brand-primary focus:border-transparent
    disabled:opacity-50 disabled:cursor-not-allowed`;

  const labelClassName = 'block text-sm font-medium text-ui-text-secondary mb-1';

  return (
    <div className="space-y-6">
      {/* Header with status badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-ui-text-primary">
            Security Context Profile
          </h3>
          {profile && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                profile.is_user_modified
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
              }`}
            >
              {profile.is_user_modified ? (
                <>
                  <User className="w-3 h-3" />
                  User-Modified
                </>
              ) : (
                <>
                  <Bot className="w-3 h-3" />
                  AI-Derived
                </>
              )}
            </span>
          )}
        </div>
        {profile && (
          <span className="text-xs text-ui-text-tertiary">
            {profile.is_user_modified
              ? `Modified: ${formatDate(profile.modified_at)}`
              : `Derived: ${formatDate(profile.derived_at)}`}
          </span>
        )}
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-800 dark:text-blue-200">
          This profile helps calibrate security scan findings. For example, input validation issues
          are critical for public web apps but less important for local CLI tools.
        </p>
      </div>

      {/* Form grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Application Type */}
        <div>
          <label className={labelClassName}>Repository Type</label>
          <select
            value={applicationType}
            onChange={(e) => {
              setApplicationType(e.target.value as ApplicationType);
              setHasChanges(true);
            }}
            disabled={readOnly}
            className={selectClassName}
          >
            {Object.entries(APPLICATION_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Deployment Exposure */}
        <div>
          <label className={labelClassName}>Deployment Exposure</label>
          <select
            value={deploymentExposure}
            onChange={(e) => {
              setDeploymentExposure(e.target.value as DeploymentExposure);
              setHasChanges(true);
            }}
            disabled={readOnly}
            className={selectClassName}
          >
            {Object.entries(DEPLOYMENT_EXPOSURE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Authentication Model */}
        <div>
          <label className={labelClassName}>Authentication Model</label>
          <select
            value={authenticationModel}
            onChange={(e) => {
              setAuthenticationModel(e.target.value as AuthenticationModel);
              setHasChanges(true);
            }}
            disabled={readOnly}
            className={selectClassName}
          >
            {Object.entries(AUTHENTICATION_MODEL_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Data Sensitivity */}
        <div>
          <label className={labelClassName}>Data Sensitivity</label>
          <select
            value={dataSensitivity}
            onChange={(e) => {
              setDataSensitivity(e.target.value as DataSensitivity);
              setHasChanges(true);
            }}
            disabled={readOnly}
            className={selectClassName}
          >
            {Object.entries(DATA_SENSITIVITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Input Sources (multi-select as checkboxes) */}
      <div>
        <label className={labelClassName}>Input Sources (select all that apply)</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          {Object.entries(INPUT_SOURCE_LABELS).map(([value, label]) => (
            <label
              key={value}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                inputSources.has(value as InputSource)
                  ? 'border-brand-primary bg-brand-primary/10 dark:bg-brand-primaryDarkMode/10'
                  : 'border-ui-border-primary hover:bg-ui-background-tertiary'
              } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={inputSources.has(value as InputSource)}
                onChange={() => handleInputSourceToggle(value as InputSource)}
                disabled={readOnly}
                className="w-4 h-4 text-brand-primary border-ui-border-primary rounded focus:ring-brand-primary"
              />
              <span className="text-sm text-ui-text-secondary">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Detected Frameworks (read-only) */}
      {profile?.detected_frameworks && profile.detected_frameworks.length > 0 && (
        <div>
          <label className={labelClassName}>Detected Frameworks</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {profile.detected_frameworks.map((framework) => (
              <span
                key={framework}
                className="px-2 py-1 text-xs font-medium bg-ui-background-tertiary text-ui-text-secondary rounded-md"
              >
                {framework}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Context Notes */}
      <div>
        <label className={labelClassName}>Additional Context (Notes)</label>
        <textarea
          value={contextNotes}
          onChange={(e) => {
            setContextNotes(e.target.value);
            setHasChanges(true);
          }}
          disabled={readOnly}
          placeholder="Add any additional context about your repository's security requirements..."
          rows={3}
          className={`${selectClassName} resize-none`}
        />
      </div>

      {/* Save button */}
      <div className="flex justify-end pt-4 border-t border-ui-border-primary">
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges || readOnly}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-black
                     bg-brand-primary hover:bg-brand-primaryDark
                     disabled:opacity-50 disabled:cursor-not-allowed
                     rounded-lg transition-colors"
        >
          {isSaving ? (
            <>
              <Spinner size={16} tone="current" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ApplicationProfileEditor;
